import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { appLanguages, defaultLanguage } from "utils/Currency&Language";

import en from "./translations/en";
import nl from "./translations/nl";

export const LANGUAGE_KEY = "satyam-language";

const translations = {
  en,
  nl,
};

const LanguageContext = createContext({
  language: defaultLanguage,
  locale: defaultLanguage.locale,
  setLanguage: () => {},
  t: (key) => key,
});

const getNestedValue = (source, path) =>
  path.split(".").reduce((value, part) => value?.[part], source);

const interpolate = (value, params = {}) =>
  Object.keys(params).reduce(
    (text, key) => text.replaceAll(`{{${key}}}`, params[key]),
    value,
  );

const getBrowserLanguage = () => {
  const browserLanguages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];
  const browserLocale = browserLanguages.find((locale) =>
    locale?.toLowerCase().startsWith("nl"),
  );

  if (!browserLocale) return defaultLanguage;

  return appLanguages.find((item) => item.locale === "nl") || defaultLanguage;
};

const getStoredLanguage = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(LANGUAGE_KEY));
    const locale = stored?.locale || stored?.code;

    return appLanguages.find((item) => item.locale === locale) || getBrowserLanguage();
  } catch {
    return getBrowserLanguage();
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getStoredLanguage);

  useEffect(() => {
    setLanguageState(getStoredLanguage());
  }, []);

  useEffect(() => {
    document.documentElement.lang = language?.locale || defaultLanguage.locale;
  }, [language]);

  const setLanguage = useCallback((newLanguage) => {
    const nextLanguage =
      appLanguages.find((item) => item.locale === newLanguage?.locale) ||
      defaultLanguage;

    localStorage.setItem(LANGUAGE_KEY, JSON.stringify(nextLanguage));
    setLanguageState(nextLanguage);
    document.documentElement.lang = nextLanguage.locale;
  }, []);

  const value = useMemo(() => {
    const locale = language?.locale || defaultLanguage.locale;

    const t = (key, params) => {
      const translated =
        getNestedValue(translations[locale], key) ||
        getNestedValue(translations[defaultLanguage.locale], key);

      if (typeof translated !== "string") return key;

      return interpolate(translated, params);
    };

    return {
      language,
      locale,
      setLanguage,
      t,
    };
  }, [language, setLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => useContext(LanguageContext);
